import { useCallback, useEffect, useRef, useState } from 'react';
import { GripVertical } from 'lucide-react';
import { fetchBoard, moveOpportunity, type BoardColumn, type Opportunity } from '../api';
import {
    SITE_HEALTH_LABEL, SITE_HEALTH_TERMS, NOT_A_LEAD,
    siteHealthOf, hostOf, callGuide, NASTA_REPLIK,
} from './siteHealth';
import { ScrollStrip } from './ScrollStrip';
import { useSynligtIntervall } from '../hooks/useSynligtIntervall';

/**
 * PipelineBoard (SCC-25) — drag-bar kanban över en pipelines stages.
 * Native HTML5 drag-and-drop (inga externa beroenden). Optimistisk flytt:
 * kortet flyttas direkt i UI:t, API-anropet sker i bakgrunden och rullas
 * tillbaka vid fel. Klick på ett kort öppnar kontaktens tråd via onSelectContact.
 */

interface PipelineBoardProps {
    pipelineId: string;
    /** Fritextfilter — matchar titel, kontaktnamn, IG, mail, telefon, adress. */
    search?: string;
    onSelectContact?: (opportunity: Opportunity) => void;
}

/** Bara det som faktiskt är en affärssignal. MOVED är städning, och
 *  UNREACHABLE/MISDIRECT vet vi inte nog om — hålls utanför leadräkningen. */
function hasBrokenSite(opp: Opportunity): boolean {
    const sh = siteHealthOf(opp.contact?.custom);
    return !!sh && !NOT_A_LEAD.has(sh.verdict);
}

/** Pågående samtal: gästen har skrivit något, och kortet ligger inte i en kolumn där
 *  samtalet är över eller vilar. Fram till 30 sep räknades alla som någonsin skrivit,
 *  arkivet inräknat, och knappen visade 79 när 19 faktiskt pågick. */
const SAMTAL_SLUT = new Set(['Arkiv', 'Tyst, ring kallt', 'Bokad', 'Betald', 'Avböjt', 'Genomförd']);
function harSamtal(opp: Opportunity, kolumn: string): boolean {
    if (SAMTAL_SLUT.has(kolumn)) return false;
    const cu = opp.contact?.custom;
    return Number(cu?.ce_gastmeddelanden ?? cu?.ce_messages_in ?? 0) > 0;
}

function matchesSearch(opp: Opportunity, q: string): boolean {
    const cu = opp.contact?.custom;
    const sh = siteHealthOf(opp.contact?.custom);
    const shText = sh
        ? `${sh.verdict} ${SITE_HEALTH_LABEL[sh.verdict] ?? ''} ${SITE_HEALTH_TERMS[sh.verdict] ?? ''} ${sh.evidence ?? ''}`
        : '';
    return [opp.title, opp.contact?.name, opp.contact?.email, opp.contact?.phone,
            cu?.instagram, cu?.website, cu?.address, cu?.ce_message, cu?.ce_country, shText]
        .some((v) => typeof v === 'string' && v.toLowerCase().includes(q));
}

/**
 * Cold Experience-leads är gäster, inte företag. De har inga betyg, ingen
 * Instagram och ingen adress. Korten visar i stället vad gästen vill, när,
 * och vad hon skrev. Fälten kommer ur contacts.custom (ce_*), satta av
 * speglingen från ce_leads.
 */
const CE_KANAL: Record<string, string> = {
    whatsapp: '💬 WhatsApp', messenger: '📨 Messenger', email: '✉️ Mejl',
    web: '🌐 Webbformulär', other: 'okänd kanal',
};

/** Landskod till flagga. Regional indicator-tecken, alltså A→🇦 osv.
 *  Gustav ska se på en halv sekund om gästen sitter i Sydafrika eller Storbritannien,
 *  för det avgör både tidszon och om samtalet är värt att ringa nu. */
function flagga(kod?: string | null): string {
    const k = (kod ?? '').trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(k)) return '';
    return String.fromCodePoint(...[...k].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

const LAND_NAMN: Record<string, string> = {
    ZA: 'Sydafrika', GB: 'Storbritannien', IE: 'Irland', NL: 'Nederländerna',
    DE: 'Tyskland', PL: 'Polen', AE: 'Förenade Arabemiraten', US: 'USA',
    IN: 'Indien', DK: 'Danmark', CH: 'Schweiz', SE: 'Sverige', NO: 'Norge',
    FI: 'Finland', FR: 'Frankrike', ES: 'Spanien', IT: 'Italien', BE: 'Belgien',
    AT: 'Österrike', AU: 'Australien', CA: 'Kanada',
};

/** Metas formulär skickar en standardmening i gästens namn, och reaktioner och bilder
 *  loggas som platshållare. Inget av det är något gästen själv har sagt. */
function arEgnaOrd(t?: unknown): t is string {
    if (typeof t !== 'string') return false;
    const s = t.trim();
    if (s.length < 4) return false;
    if (/^hello!? i filled (in|out) your form/i.test(s)) return false;
    if (/^\[(reaction|bild|image|sticker)/i.test(s)) return false;
    if (/^\S+@\S+\.\S+$/.test(s)) return false;
    return true;
}

type CeCustom = Record<string, any>;

/** Kortets andra rad: var samtalet står just nu, i klartext. Bygger på kolumnen,
 *  som i sin tur är vem som har bollen (ce_kolumn_for i databasen, 29 sep). */
function ceLage(ce: CeCustom, kolumn: string): { text: string; farg: string } {
    const inAt = ce.ce_last_inbound_at ? Date.parse(ce.ce_last_inbound_at) : NaN;
    const utAt = ce.ce_last_outbound_at ? Date.parse(ce.ce_last_outbound_at) : NaN;
    const senast = ce.ce_last_message_at ?? ce.ce_received_at;
    // Ett samtal som kunden loggat i portalen går före allt annat i de två kolumner där någon
    // ska ringas. Fram till 3 okt stod det "Väntar på samtal" även när Gustav redan hade ringt.
    const samtal = ceSamtalslage(ce, kolumn);
    if (samtal) return samtal;
    // Kundens egen kontakt (Nora avstängd för hand): ingen har lämnat över något, så kortet
    // ska inte säga "väntar på samtal". Boukje Nienhuis stod så i tre veckor.
    if (ce.ce_egen_kontakt && (kolumn === 'Överlämnad' || kolumn === 'Tyst, ring kallt')) {
        return { text: '✋ Gustavs egen kontakt · Nora är avstängd', farg: '#FF9F0A' };
    }
    if (ce.ce_human_active && kolumn !== 'Arkiv') return { text: '✋ Gustav skriver själv i tråden', farg: '#FF9F0A' };
    switch (kolumn) {
        case 'Överlämnad':
            return {
                text: `📞 Väntar på samtal sedan ${ceTid(ce.ce_handed_off_at ?? senast) || 'okänt'}${ce.ce_callback ? ` · passar ${ce.ce_callback}` : ''}`,
                farg: '#FF6B5B',
            };
        case 'Tyst, ring kallt':
            return { text: `🔕 Tystnade ${ceTid(ce.ce_last_inbound_at ?? senast) || 'okänt'}`, farg: '#A0A8B8' };
        case 'Ny':
            return { text: `🆕 Formulär ${ceTid(ce.ce_received_at) || ''} · ingen kontakt än`, farg: '#5AC8FA' };
        case 'Arkiv':
            return { text: `Arkiverad · senast aktiv ${ceTid(senast) || 'okänt'}`, farg: 'rgba(255,255,255,0.45)' };
        case 'Bokad':
            return { text: '✅ Bokad', farg: '#32D74B' };
        case 'Betald':
            return { text: '💶 Betald', farg: '#32D74B' };
        default: {
            if (!Number.isNaN(inAt) && (Number.isNaN(utAt) || inAt > utAt)) {
                return { text: `💬 Gästen skrev ${ceTid(ce.ce_last_inbound_at)} · Nora svarar`, farg: '#32D74B' };
            }
            if (Number(ce.ce_messages_in ?? 0) > 0) {
                return { text: `⏳ Nora väntar på svar sedan ${ceTid(ce.ce_last_outbound_at ?? senast)}`, farg: '#5AC8FA' };
            }
            const via = ce.ce_channel === 'email' ? 'Mejl skickat' : 'Nora har skrivit';
            return { text: `⏳ ${via} ${ceTid(ce.ce_last_outbound_at ?? senast)} · inget svar än`, farg: '#5AC8FA' };
        }
    }
}

/** Läget efter ett loggat samtal. Samma regler som i portalen (portal/src/components/SccRader.tsx). */
function ceSamtalslage(ce: CeCustom, kolumn: string): { text: string; farg: string } | null {
    const antal = Number(ce.ce_ringd_antal ?? 0);
    if (!antal || !ce.ce_ringd_senast || (kolumn !== 'Överlämnad' && kolumn !== 'Tyst, ring kallt')) return null;
    const nar = ceTid(ce.ce_ringd_senast);
    // Skrev gästen igen efter samtalet ska någon höra av sig på nytt.
    const skrevEfter = !!ce.ce_last_inbound_at && Date.parse(ce.ce_last_inbound_at) > Date.parse(ce.ce_ringd_senast);
    if (ce.ce_ringd_utfall === 'svarade') {
        return skrevEfter
            ? { text: `💬 Gästen skrev igen efter samtalet ${nar}`, farg: '#FF6B5B' }
            : { text: `📞 Ringd ${nar}, de pratade · väntar på besked`, farg: '#32D74B' };
    }
    if (ce.ce_ringd_utfall === 'svarade_inte') {
        return { text: `📞 Ringd ${antal === 1 ? '1 gång' : `${antal} gånger`} utan svar · senast ${nar}`, farg: '#FF6B5B' };
    }
    return null;
}

/** Läget för leads på andra bräden än Cold Experience (Vinnie). Ingen Nora, inga
 *  CE-kolumner: bara om vi hört av oss och om kunden svarat. */
function ceLageAnnan(ce: CeCustom): { text: string; farg: string } {
    if (!ce.ce_contacted) return { text: `🆕 Formulär ${ceTid(ce.ce_received_at) || ''} · ingen kontakt än`, farg: '#5AC8FA' };
    if (ce.ce_lead_replied) return { text: `💬 Kunden har svarat · senast ${ceTid(ce.ce_last_message_at) || 'okänt'}`, farg: '#32D74B' };
    return { text: `⏳ Kontaktad ${ceTid(ce.ce_last_message_at) || ''} · inget svar än`, farg: '#A0A8B8' };
}

/** Kortets tredje rad: det vi vet om resan. Tomt betyder att Nora inte fått veta något än. */
function ceVet(ce: CeCustom): string {
    const barn = Array.isArray(ce.ce_children) && ce.ce_children.length ? `barn ${ce.ce_children.join(', ')}` : null;
    return [
        [ce.ce_adults ? `${ce.ce_adults} vuxna` : null, barn].filter(Boolean).join(' + ') || null,
        ce.ce_travel_when || null,
        ce.ce_days ? `${ce.ce_days} dagar` : null,
        ce.ce_departure ? `flyg ${ce.ce_departure}` : null,
        ce.ce_price_eur ? `sett €${ce.ce_price_eur}` : null,
    ].filter(Boolean).join(' · ');
}

/** Kortets fjärde rad: något gästen själv skrivit, aldrig formulärets standardtext. */
function ceCitat(ce: CeCustom): string | null {
    for (const t of [ce.ce_egna_ord, ce.ce_last_inbound, ce.ce_message]) if (arEgnaOrd(t)) return t.trim();
    return null;
}

/** Senaste aktivitet, för sortering av CE-kolumnerna: nyast överst. */
function ceSenast(o: Opportunity): number {
    const cu = o.contact?.custom;
    const t = Date.parse(cu?.ce_last_message_at ?? cu?.ce_received_at ?? '');
    return Number.isNaN(t) ? 0 : t;
}

function ceTid(iso?: string): string {
    if (!iso) return '';
    const m = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const idag = new Date();
    const sammaDag = d.toDateString() === idag.toDateString();
    const igar = new Date(idag.getTime() - 864e5).toDateString() === d.toDateString();
    const klocka = `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    if (sammaDag) return `idag ${klocka}`;
    if (igar) return `igår ${klocka}`;
    return `${d.getDate()} ${m[d.getMonth()]} ${klocka}`;
}

const glassCol: React.CSSProperties = {
    flex: '0 0 260px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14,
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minHeight: 200,
};

function scoreBg(score: number): string {
    if (score >= 85) return '#7ee787'; // tier A
    if (score >= 70) return '#f0c674'; // tier B
    return '#9aa0a6'; // tier C
}

const flowLabel: Record<string, string> = {
    manual: 'Manuell / DM',
    form: 'Formulär',
    online: 'Online-bokning',
};

type Tier = 'A' | 'B' | 'C';

function tierOf(score: number): Tier {
    if (score >= 85) return 'A';
    if (score >= 70) return 'B';
    return 'C';
}

function toolBtn(active: boolean): React.CSSProperties {
    return {
        fontSize: 12,
        padding: '4px 10px',
        borderRadius: 8,
        cursor: 'pointer',
        border: '1px solid rgba(255,255,255,0.14)',
        background: active ? 'rgba(120,180,255,0.22)' : 'rgba(255,255,255,0.05)',
        color: 'inherit',
    };
}

const cardLink: React.CSSProperties = {
    color: '#9ecbff',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    // display:block krävs — på inline-element ignoreras overflow, och en enda
    // lång URL sprängde hela kolumnbredden (Ink Brothers 271-teckens fbclid-URL).
    display: 'block',
    maxWidth: '100%',
};

export function PipelineBoard({ pipelineId, search, onSelectContact }: PipelineBoardProps) {
    const [columns, setColumns] = useState<BoardColumn[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dropStage, setDropStage] = useState<string | null>(null);
    const [drag, setDrag] = useState<{ oppId: string; title: string; x: number; y: number } | null>(null);
    const colsRef = useRef<HTMLDivElement>(null);
    const [sortMode, setSortMode] = useState<'score' | 'name' | 'ort'>('score');
    const [tierFilter, setTierFilter] = useState<'all' | Tier>('all');
    // Samtalsfiltret. "Kontaktad" och "pratar med oss" är två helt olika saker,
    // och med hundra kort i brädet drunknar de tjugo som faktiskt svarat.
    const [baraSamtal, setBaraSamtal] = useState(false);
    // Arkiv är hopfällt som standard: där ligger allt som varit tyst i sju dagar.
    // Vid sökning visas träffarna ändå, så ingen gäst går att tappa bort.
    const [visaArkiv, setVisaArkiv] = useState(false);
    // Cold Experience-brädet känns igen på sin egen kolumn. Andra bräden med leads ur
    // ce_leads (Vinnie) får inte Noras och resans rader.
    const arCeBrade = columns.some((c) => c.stage.name === 'Nora pratar');
    const [brokenOnly, setBrokenOnly] = useState(false);
    const [openGuide, setOpenGuide] = useState<Set<string>>(new Set());

    const load = useCallback(async () => {
        try {
            setColumns(await fetchBoard(pipelineId));
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte hämta pipeline');
        }
        setLoading(false);
    }, [pipelineId]);

    useEffect(() => { void load(); }, [load]);
    // Boarden ska aldrig visa gammal data, men den ska inte heller hämta hela
    // leadlistan en gång i minuten hela natten åt ingen. Hooken tickar bara när
    // fliken syns, och hämtar direkt när man kommer tillbaka till den, så det här
    // är både billigare och färskare än det gamla intervallet.
    useSynligtIntervall(() => void load(), 60_000);

    // ── Alex (navigate_ui): öppna ett specifikt kontaktkort. Om boarden inte
    //    hunnit ladda än sparas önskan och försöks igen när kolumnerna kommer. ──
    const pendingContactRef = useRef<string | null>(null);
    const onSelectContactRef = useRef(onSelectContact);
    onSelectContactRef.current = onSelectContact;

    useEffect(() => {
        const tryOpen = (contactId: string, cols: BoardColumn[]): boolean => {
            for (const col of cols) {
                const opp = col.opportunities.find(o => o.contact?.id === contactId);
                if (opp) { onSelectContactRef.current?.(opp); return true; }
            }
            return false;
        };
        const onOpenContact = (e: Event) => {
            const contactId = (e as CustomEvent<{ contactId: string }>).detail?.contactId;
            if (!contactId) return;
            setColumns(cols => {
                if (!tryOpen(contactId, cols)) pendingContactRef.current = contactId;
                return cols;
            });
        };
        window.addEventListener('scc:open-contact', onOpenContact);
        return () => window.removeEventListener('scc:open-contact', onOpenContact);
    }, []);

    // Rundturen: öppna "bästa" kortet (första kolumnen med kort, översta kortet)
    useEffect(() => {
        const onTourOpen = () => {
            setColumns(cols => {
                for (const col of cols) {
                    if (col.opportunities.length > 0) {
                        onSelectContactRef.current?.(col.opportunities[0]);
                        break;
                    }
                }
                return cols;
            });
        };
        window.addEventListener('scc:tour-open-card', onTourOpen);
        return () => window.removeEventListener('scc:tour-open-card', onTourOpen);
    }, []);

    useEffect(() => {
        const pending = pendingContactRef.current;
        if (!pending) return;
        for (const col of columns) {
            const opp = col.opportunities.find(o => o.contact?.id === pending);
            if (opp) {
                pendingContactRef.current = null;
                onSelectContactRef.current?.(opp);
                return;
            }
        }
    }, [columns]);

    // Optimistisk flytt av ett kort till en annan stage.
    const moveCard = useCallback(async (oppId: string, stageId: string) => {
        let moved: Opportunity | undefined;
        const prev = columns;
        const next = columns.map((col) => {
            const idx = col.opportunities.findIndex((o) => o.id === oppId);
            if (idx >= 0) moved = col.opportunities[idx];
            return { ...col, opportunities: col.opportunities.filter((o) => o.id !== oppId) };
        });
        if (!moved || moved.stage_id === stageId) return;
        const target = next.find((c) => c.stage.id === stageId);
        if (target) target.opportunities = [{ ...moved, stage_id: stageId }, ...target.opportunities];
        setColumns(next);
        try {
            await moveOpportunity(oppId, stageId);
        } catch {
            setColumns(prev); // rollback vid fel
        }
    }, [columns]);

    // Pekar-drag från kortets grepp — funkar på touch (mobil) OCH mus (desktop).
    // Greppet har touch-action:none så fingret drar kortet i stället för att skrolla.
    const startDrag = useCallback((e: React.PointerEvent, opp: Opportunity) => {
        e.preventDefault();
        e.stopPropagation();
        const oppId = opp.id;
        const body = document.body.style;
        // Lås markering + scroll så browsern inte kapar gesten (annars markeras text / draget hänger).
        body.setProperty('user-select', 'none');
        body.setProperty('-webkit-user-select', 'none');
        body.setProperty('touch-action', 'none');
        body.setProperty('overflow', 'hidden');
        setDrag({ oppId, title: opp.title, x: e.clientX, y: e.clientY });

        const ptr = { x: e.clientX, y: e.clientY };
        let overStage: string | null = null;

        // Auto-scrolla kolumnlisten när fingret är nära kanten (mobil: mål-kolumnen ligger off-screen).
        let raf = 0;
        const tick = () => {
            const c = colsRef.current;
            if (c) {
                const r = c.getBoundingClientRect();
                const EDGE = 72, SPEED = 16;
                if (ptr.x > r.right - EDGE) c.scrollLeft += SPEED;
                else if (ptr.x < r.left + EDGE) c.scrollLeft -= SPEED;
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);

        const onMove = (ev: PointerEvent) => {
            ev.preventDefault();
            ptr.x = ev.clientX; ptr.y = ev.clientY;
            setDrag((d) => (d ? { ...d, x: ev.clientX, y: ev.clientY } : d));
            const el = document.elementFromPoint(ev.clientX, ev.clientY) as Element | null;
            const colEl = el?.closest('[data-stage-id]') as HTMLElement | null;
            overStage = colEl?.getAttribute('data-stage-id') ?? null;
            setDropStage(overStage);
        };
        const finish = () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', finish);
            window.removeEventListener('pointercancel', finish);
            body.removeProperty('user-select');
            body.removeProperty('-webkit-user-select');
            body.removeProperty('touch-action');
            body.removeProperty('overflow');
            if (overStage) void moveCard(oppId, overStage);
            setDrag(null);
            setDropStage(null);
        };
        window.addEventListener('pointermove', onMove, { passive: false });
        window.addEventListener('pointerup', finish);
        window.addEventListener('pointercancel', finish);
    }, [moveCard]);

    if (loading) return <p style={{ opacity: 0.6 }}>Laddar pipeline…</p>;
    if (error) return <p style={{ color: '#ff6b6b' }}>Fel: {error}</p>;

    const q = search?.trim().toLowerCase() ?? '';
    const viewColumns = columns.map((col) => {
        let filtered = tierFilter === 'all'
            ? col.opportunities
            : col.opportunities.filter((o) => {
                const s = o.contact?.custom?.score;
                return typeof s === 'number' && tierOf(s) === tierFilter;
            });
        if (brokenOnly) filtered = filtered.filter(hasBrokenSite);
        if (baraSamtal) filtered = filtered.filter((o) => harSamtal(o, col.stage.name));
        if (q) filtered = filtered.filter((o) => matchesSearch(o, q));
        const areaOf = (o: Opportunity) => String(o.contact?.custom?.area ?? 'övrigt');
        const ordered = [...filtered].sort((a, b) => {
            // Cold Experience-kort har ingen score. Nyast aktivitet överst är det som
            // betyder något för Gustav.
            if (sortMode === 'score' && a.contact?.custom?.ce_lead_id && b.contact?.custom?.ce_lead_id) return ceSenast(b) - ceSenast(a);
            if (sortMode === 'score') return (b.contact?.custom?.score ?? -1) - (a.contact?.custom?.score ?? -1);
            if (sortMode === 'ort') {
                const c = areaOf(a).localeCompare(areaOf(b), 'sv');
                return c !== 0 ? c : a.title.localeCompare(b.title, 'sv');
            }
            return a.title.localeCompare(b.title, 'sv');
        });
        return { ...col, opportunities: ordered };
    });

    return (
        <div className="pl-board">
            <ScrollStrip className="pl-toolbar" aria-label="Sortering och filter">
                {([['score', '↓ Score'], ['name', 'A–Ö'], ['ort', 'Ort']] as const).map(([mode, label]) => (
                    <button key={mode} onClick={() => setSortMode(mode)} style={toolBtn(sortMode === mode)}>
                        {label}
                    </button>
                ))}
                <span style={{ opacity: 0.4, fontSize: 12, marginLeft: 4 }}>Tier:</span>
                {(['all', 'A', 'B', 'C'] as const).map((t) => (
                    <button key={t} onClick={() => setTierFilter(t)} style={toolBtn(tierFilter === t)}>
                        {t === 'all' ? 'Alla' : t}
                    </button>
                ))}
                {(() => {
                    // Samma princip som trasig-webb-knappen: siffran räknas på hela
                    // brädet, annars ändras den av att man klickar på knappen.
                    const alla = columns.flatMap((c) => c.opportunities);
                    const medSamtal = columns.reduce((n, c) => n + c.opportunities.filter((o) => harSamtal(o, c.stage.name)).length, 0);
                    if (medSamtal === 0 || medSamtal === alla.length) return null;
                    return (
                        <button
                            data-aktiv={baraSamtal ? 'true' : undefined}
                            onClick={() => setBaraSamtal((v) => !v)}
                            title="Gäster som skrivit och vars samtal inte är arkiverat, tyst eller avslutat"
                            style={{
                                ...toolBtn(baraSamtal), marginLeft: 4,
                                borderColor: baraSamtal ? 'rgba(50,215,75,0.55)' : 'rgba(50,215,75,0.3)',
                                background: baraSamtal ? 'rgba(50,215,75,0.22)' : 'rgba(50,215,75,0.08)',
                            }}
                        >
                            💬 Pågående samtal <span style={{ opacity: 0.7 }}>{medSamtal}</span>
                        </button>
                    );
                })()}
                {(() => {
                    // Räknas på hela brädet, inte på den filtrerade vyn — annars
                    // skulle siffran ändras av att man klickar på knappen.
                    const alla = columns.flatMap((c) => c.opportunities);
                    const broken = alla.filter(hasBrokenSite).length;
                    // Ett filter som träffar allt filtrerar ingenting. På brädet
                    // "Webb — trasiga sajter" är varje kort redan en träff, och
                    // då är knappen bara en siffra som ser ut att gå att klicka på.
                    if (broken === 0 || broken === alla.length) return null;
                    return (
                        <button
                            onClick={() => setBrokenOnly((v) => !v)}
                            title="Visa bara prospekt vars hemsida är trasig, parkerad eller borta"
                            style={{ ...toolBtn(brokenOnly), marginLeft: 4, borderColor: brokenOnly ? 'rgba(255,138,138,0.5)' : 'rgba(255,138,138,0.28)', background: brokenOnly ? 'rgba(255,138,138,0.22)' : 'rgba(255,138,138,0.08)' }}
                        >
                            ● Trasig webb <span style={{ opacity: 0.7 }}>{broken}</span>
                        </button>
                    );
                })()}
                {(() => {
                    const costs = columns.flatMap((c) => c.opportunities)
                        .map((o) => o.contact?.custom?.research_cost_usd)
                        .filter((v): v is number => typeof v === 'number');
                    if (costs.length === 0) return null;
                    const total = costs.reduce((a, b) => a + b, 0);
                    return (
                        <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.5, whiteSpace: 'nowrap' }}>
                            Prospektkostnad: ~{(total * 10.5).toFixed(0)} kr totalt · {((total / costs.length) * 10.5).toFixed(2)} kr/kort ({costs.length} mätta)
                        </span>
                    );
                })()}
            </ScrollStrip>
            <div ref={colsRef} className="pl-columns" style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 8 }}>
            {viewColumns.map((col) => (
                <div
                    key={col.stage.id}
                    className="pl-col"
                    data-stage-id={col.stage.id}
                    style={{
                        ...glassCol,
                        outline: dropStage === col.stage.id ? '2px solid rgba(120,180,255,0.6)' : 'none',
                        transition: 'outline 0.1s',
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, fontWeight: 600, opacity: 0.85 }}>
                        <span>{col.stage.name}</span>
                        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {col.stage.name === 'Arkiv' && !q && (
                                <button
                                    onClick={() => setVisaArkiv((v) => !v)}
                                    style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, color: 'rgba(255,255,255,0.7)', fontSize: 11, padding: '1px 7px', cursor: 'pointer' }}
                                >
                                    {visaArkiv ? 'Dölj' : 'Visa'}
                                </button>
                            )}
                            <span style={{ opacity: 0.5 }}>{col.opportunities.length}</span>
                        </span>
                    </div>
                    {col.stage.name === 'Arkiv' && !visaArkiv && !q && (
                        <div style={{ fontSize: 11, opacity: 0.45, lineHeight: 1.4 }}>
                            Tysta i sju dagar eller avböjda. Skriver gästen igen kommer kortet tillbaka av sig självt.
                        </div>
                    )}

                    {(col.stage.name === 'Arkiv' && !visaArkiv && !q ? [] : col.opportunities).map((opp) => (
                        <div
                            key={opp.id}
                            onClick={() => opp.contact?.id && onSelectContact?.(opp)}
                            style={{
                                position: 'relative',
                                background: 'rgba(255,255,255,0.06)',
                                border: '1px solid rgba(255,255,255,0.10)',
                                borderRadius: 10,
                                padding: '10px 12px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                WebkitTouchCallout: 'none',
                                opacity: drag?.oppId === opp.id ? 0.4 : 1,
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                                    <button
                                        onPointerDown={(e) => startDrag(e, opp)}
                                        onClick={(e) => e.stopPropagation()}
                                        title="Dra för att flytta"
                                        aria-label="Dra för att flytta"
                                        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.42)', cursor: 'grab', display: 'flex', alignItems: 'center', padding: 6, margin: '-6px -2px -6px -6px', flex: '0 0 auto', touchAction: 'none' }}
                                    >
                                        <GripVertical size={17} />
                                    </button>
                                    <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opp.title}</div>
                                </div>
                                {typeof opp.contact?.custom?.score === 'number' && (
                                    <span
                                        title="Prospect-score"
                                        style={{
                                            fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
                                            background: scoreBg(opp.contact.custom.score), color: '#0b0b0f', whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {opp.contact.custom.score}
                                    </span>
                                )}
                            </div>
                            {opp.contact?.custom?.ce_lead_id && (() => {
                                // Samma fyra rader på varje gästkort, i samma ordning (29 sep):
                                // var ifrån och hur, läget i klartext, vad vi vet, vad gästen själv skrev.
                                const ce = opp.contact.custom;
                                // ce_leads bär även Vinnies leads (egen tenant, eget bräde). Nora och
                                // resan hör bara till Cold Experience; Vinnie svarar själv. 29 sep stod
                                // det "Nora väntar på svar" på hans kort.
                                const lage = arCeBrade ? ceLage(ce, col.stage.name) : ceLageAnnan(ce);
                                const vet = arCeBrade ? ceVet(ce) : '';
                                const citat = ceCitat(ce);
                                // Kanalen där gästen senast skrev, inte formulärvalet: Gwenllian valde mejl
                                // men pratar med Nora på WhatsApp (30 sep).
                                const kanalKod = ce.ce_senaste_kanal ?? ce.ce_channel;
                                const kanal = (kanalKod && kanalKod !== 'other' && CE_KANAL[kanalKod]) || null;
                                const gastmeddelanden = Number(ce.ce_gastmeddelanden ?? ce.ce_messages_in ?? 0);
                                return (
                                    <>
                                        {/* Rad 1, fakta om gästen (30 sep): samma stil för allt, inga färger.
                                            Färg används bara på rad 2, och där betyder den kolumnen. */}
                                        <div style={{ fontSize: 11, marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', color: 'rgba(255,255,255,0.65)' }}>
                                            {[
                                                ce.ce_country ? (
                                                    <span key="land" title={LAND_NAMN[String(ce.ce_country).toUpperCase()] ?? String(ce.ce_country)}>
                                                        {flagga(ce.ce_country)} {String(ce.ce_country).toUpperCase()}
                                                    </span>
                                                ) : null,
                                                kanal ? <span key="kanal" title="Kanalen där gästen senast skrev">{kanal}</span> : null,
                                                gastmeddelanden > 0 ? (
                                                    <span key="antal" title="Meddelanden gästen själv skickat. Formuläret räknas en gång.">
                                                        💬 {gastmeddelanden}
                                                    </span>
                                                ) : null,
                                                ce.ce_intent === 'yes' ? <span key="vinter" title="Vill komma i vinter">🔥 i vinter</span> : null,
                                            ].filter(Boolean).flatMap((el, i) => (i === 0 ? [el] : [<span key={`p${i}`} style={{ opacity: 0.4 }}>·</span>, el]))}
                                        </div>
                                        <div style={{ fontSize: 11.5, fontWeight: 600, marginTop: 6, color: lage.farg, lineHeight: 1.35 }}>
                                            {lage.text}
                                        </div>
                                        {arCeBrade && (
                                            <div style={{ fontSize: 11, marginTop: 4, opacity: vet ? 0.85 : 0.4, lineHeight: 1.35 }}>
                                                {vet || 'Vet inget om resan än'}
                                            </div>
                                        )}
                                        <div style={{ fontSize: 11, marginTop: 4, fontStyle: citat ? 'italic' : 'normal', opacity: citat ? 0.7 : 0.4, lineHeight: 1.4 }}>
                                            {citat
                                                ? `”${citat.slice(0, 110)}${citat.length > 110 ? '…' : ''}”`
                                                : 'Har inte skrivit något eget än'}
                                        </div>
                                        {/* Senaste anteckningen från kundens portal, på alla bräden med portalkunder. Visas bara när det finns en. */}
                                        {typeof ce.ce_anteckning === 'string' && ce.ce_anteckning.trim() && (
                                            <div style={{ fontSize: 11, marginTop: 4, lineHeight: 1.4, color: 'rgba(255,255,255,0.75)' }}
                                                title={`Anteckning i kundportalen${ce.ce_anteckning_at ? ` ${ceTid(ce.ce_anteckning_at)}` : ''}${Number(ce.ce_anteckningar ?? 0) > 1 ? ` · ${ce.ce_anteckningar} totalt` : ''}`}>
                                                📝 {ce.ce_anteckning.slice(0, 110)}{ce.ce_anteckning.length > 110 ? '…' : ''}
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                            {opp.contact?.custom?.rating && (
                                <div style={{ fontSize: 12, opacity: 0.6, marginTop: 3 }}>
                                    {opp.contact.custom.rating} ★{opp.contact?.custom?.reviews ? ` (${opp.contact.custom.reviews})` : ''}
                                </div>
                            )}
                            {opp.contact?.custom?.booking_flow && (
                                <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                                    <span>{flowLabel[opp.contact.custom.booking_flow] ?? opp.contact.custom.booking_flow}</span>
                                    <span style={{ opacity: 0.4 }}>·</span>
                                    <span>{opp.contact?.email ? 'email + IG DM' : 'IG DM'}</span>
                                </div>
                            )}
                            {opp.contact?.custom?.address && (
                                <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }} onClick={(e) => e.stopPropagation()}>
                                    <a
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(opp.contact.custom.address)}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={cardLink}
                                    >
                                        📍 {opp.contact.custom.address}
                                    </a>
                                </div>
                            )}
                            {(opp.contact?.phone || opp.contact?.email || opp.contact?.custom?.website || opp.contact?.custom?.instagram) && (
                                <div
                                    style={{ fontSize: 11, marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {opp.contact?.custom?.instagram && (
                                        <a href={`https://instagram.com/${opp.contact.custom.instagram}`} target="_blank" rel="noreferrer" style={cardLink}>
                                            IG · @{opp.contact.custom.instagram}
                                        </a>
                                    )}
                                    {opp.contact?.phone && (
                                        <a href={`tel:${opp.contact.phone.replace(/\s+/g, '')}`} style={cardLink}>Tel · {opp.contact.phone}</a>
                                    )}
                                    {opp.contact?.email && (
                                        <a href={`mailto:${opp.contact.email}`} style={cardLink}>Mail · {opp.contact.email}</a>
                                    )}
                                    {opp.contact?.custom?.website && (() => {
                                        const sh = siteHealthOf(opp.contact?.custom);
                                        const broken = !!sh && !NOT_A_LEAD.has(sh.verdict);
                                        // MOVED och UNREACHABLE är inte bevisat trasiga — gula, inte röda.
                                        const tone = broken ? '#ff9a9a' : '#e0b978';
                                        return (
                                            <div>
                                                <a href={opp.contact.custom.website} target="_blank" rel="noreferrer"
                                                   style={{ ...cardLink, ...(sh ? { color: tone, textDecoration: broken ? 'line-through' : 'none', textDecorationColor: 'rgba(255,154,154,0.55)' } : null) }}>
                                                    {sh ? '● ' : ''}Webb · {opp.contact.custom.website.replace(/^https?:\/\/(www\.)?/, '')}
                                                </a>
                                                {sh && (() => {
                                                    const open = openGuide.has(opp.id);
                                                    const site = hostOf(opp.contact?.custom?.website);
                                                    const target = hostOf(sh.final_url);
                                                    const g = callGuide(sh.verdict, site, target);
                                                    return (
                                                        <>
                                                            <div
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setOpenGuide((prev) => {
                                                                        const next = new Set(prev);
                                                                        next.has(opp.id) ? next.delete(opp.id) : next.add(opp.id);
                                                                        return next;
                                                                    });
                                                                }}
                                                                style={{ fontSize: 11, color: tone, opacity: 0.85, marginTop: 1, cursor: 'pointer', userSelect: 'none' }}
                                                                title="Visa diagnos och öppningsreplik"
                                                            >
                                                                {open ? '▾' : '▸'} {SITE_HEALTH_LABEL[sh.verdict]}
                                                            </div>
                                                            {open && (
                                                                <div
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    style={{
                                                                        marginTop: 6, padding: '8px 10px', borderRadius: 8,
                                                                        background: 'rgba(255,255,255,0.04)',
                                                                        border: '1px solid rgba(255,255,255,0.09)',
                                                                        fontSize: 11, lineHeight: 1.45, whiteSpace: 'normal',
                                                                    }}
                                                                >
                                                                    <div style={{ opacity: 0.5, fontSize: 10, letterSpacing: 0.4 }}>DIAGNOS</div>
                                                                    <div style={{ marginBottom: 6 }}>{g.diagnos}</div>

                                                                    <div style={{ opacity: 0.5, fontSize: 10, letterSpacing: 0.4 }}>BEVIS</div>
                                                                    <div style={{ marginBottom: 6, opacity: 0.8 }}>{sh.evidence}</div>

                                                                    {/* Efter två falska positiva 14 sep är detta steg inbyggt,
                                                                        inte något man ska komma ihåg. */}
                                                                    <a href={opp.contact.custom.website} target="_blank" rel="noreferrer"
                                                                       style={{ color: '#9ecbff', textDecoration: 'none', display: 'inline-block', marginBottom: 8 }}>
                                                                        ↗ Öppna sajten och kontrollera själv först
                                                                    </a>

                                                                    <div style={{ opacity: 0.5, fontSize: 10, letterSpacing: 0.4 }}>ÖPPNING</div>
                                                                    <div style={{ fontStyle: 'italic' }}>”{g.oppning}”</div>
                                                                    <div style={{ fontStyle: 'italic', marginTop: 4, opacity: 0.75 }}>”{NASTA_REPLIK}”</div>
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                            {typeof opp.value_sek === 'number' && (
                                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                                    {opp.value_sek.toLocaleString('sv-SE')} kr
                                </div>
                            )}
                        </div>
                    ))}

                    {col.opportunities.length === 0 && (
                        <div style={{ fontSize: 12, opacity: 0.35, textAlign: 'center', padding: '16px 0' }}>Tomt</div>
                    )}
                </div>
            ))}
            </div>

            {drag && (
                <div style={{ position: 'fixed', left: drag.x + 14, top: drag.y - 12, zIndex: 1000, pointerEvents: 'none', background: 'rgba(28,30,36,0.96)', border: '1px solid rgba(120,180,255,0.5)', borderRadius: 10, padding: '9px 13px', fontSize: 14, fontWeight: 600, color: '#e8e4d8', boxShadow: '0 12px 34px rgba(0,0,0,0.55)', maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {drag.title}
                </div>
            )}
        </div>
    );
}

export default PipelineBoard;
